import React, { useMemo, useState } from 'react'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import DataTableLocal from '../../components/DataTableLocal'
import { useGetBudgetQuery, useSetSpendMutation } from '../../redux/growthosBudgetApiSlice'
import { GX_INK_500, GX_INK_900, GX_PAGE_SX } from '../growthos-shared/GrowthOsUi'

// Ported 1:1 from the real GrowthOS app's Data->Budget page (lastNMonths/RANGE_OPTIONS) —
// the backend never restricted which month can be written (PUT accepts any YYYY-MM), this
// range is purely how far back the grid renders inputs for.
const RANGE_OPTIONS = [6, 12, 24, 36]

function lastNMonths(n) {
  const out = []
  const now = new Date()
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    out.push(d.toISOString().slice(0, 7)) // YYYY-MM
  }
  return out
}

/** One editable currency cell — uncontrolled input, save-on-blur, matching the real app's
 * defaultValue+onBlur pattern (a controlled input would fight the user mid-type on refetch). */
function SpendCell({ month, subcategoryId, value, onSave }) {
  const [saved, setSaved] = useState(false)
  const [pending, setPending] = useState(false)

  const handleBlur = async (e) => {
    const v = e.target.value.trim()
    if ((value ?? '') === v) return
    setPending(true)
    try {
      await onSave(v === '' ? null : Number(v))
      setSaved(true)
      setTimeout(() => setSaved(false), 1500)
    } finally {
      setPending(false)
    }
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <TextField
        size="small"
        // Re-mount when the underlying value changes (saved edit, or a range switch) so the
        // uncontrolled input's defaultValue doesn't go stale.
        key={`${month}:${subcategoryId}:${value ?? ''}`}
        defaultValue={value ?? ''}
        placeholder="—"
        disabled={pending}
        inputMode="numeric"
        onBlur={handleBlur}
        InputProps={{
          startAdornment: <CurrencyRupeeIcon sx={{ fontSize: 12, color: 'text.disabled', mr: 0.25 }} />,
        }}
        sx={{ width: 120, '& input': { fontSize: 12, textAlign: 'right', py: 0.75 } }}
      />
      {saved && (
        <Typography sx={{ position: 'absolute', right: -18, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'success.main' }}>
          ✓
        </Typography>
      )}
    </Box>
  )
}

/**
 * GrowthOS — Marketing Budget. Month-wise spend per marketing source sub-category — powers
 * cost-per-lead/cost-per-opportunity/cost-per-customer on the (not-yet-built) Marketing
 * dashboard. No external dependency here (unlike Brands/HubSpot) — this is pure ProShip-
 * internal manual entry, so there's nothing to defer; the sub-category list itself is
 * mock-seeded on first load since no connector populates it in this migration.
 */
export default function BudgetPage() {
  const { enqueueSnackbar } = useSnackbar()
  const [rangeMonths, setRangeMonths] = useState(24)
  const months = useMemo(() => lastNMonths(rangeMonths), [rangeMonths])

  const { data, isFetching, error } = useGetBudgetQuery()
  const [setSpend] = useSetSpendMutation()

  const subcategories = data?.subcategories || []
  const spendMap = useMemo(() => {
    const m = new Map()
    for (const s of data?.spend || []) m.set(`${s.month}:${s.subcategoryId}`, s.amount)
    return m
  }, [data])

  const save = async (month, subcategoryId, amount) => {
    try {
      await setSpend({ month, subcategoryId, amount }).unwrap()
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Failed to save.', { variant: 'error' })
    }
  }

  const columns = [
    { label: '#', value: (row) => row.sortOrder, align: 'center', width: 40 },
    { label: 'Marketing Source', value: (row) => row.name, align: 'left', width: 220 },
    ...months.map((month) => ({
      label: month,
      align: 'left',
      value: (row) => (
        <SpendCell
          month={month}
          subcategoryId={row.id}
          value={spendMap.get(`${month}:${row.id}`) ?? null}
          onSave={(amount) => save(month, row.id, amount)}
        />
      ),
    })),
  ]

  return (
    <Box sx={GX_PAGE_SX}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Marketing Budget</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, maxWidth: 700 }}>
            Month-wise spend per marketing source sub-category. These numbers bifurcate the
            funnel into cost per lead, cost per opportunity and cost per customer.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Show</Typography>
          <Select size="small" value={rangeMonths} onChange={(e) => setRangeMonths(Number(e.target.value))}>
            {RANGE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n} months</MenuItem>)}
          </Select>
        </Box>
      </Box>

      {error ? (
        <Typography sx={{ fontSize: 13, color: 'error.main' }}>
          Failed to load budget — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Typography>
      ) : isFetching ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading…</Typography>
      ) : (
        <DataTableLocal columns={columns} rows={subcategories} />
      )}

      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 2 }}>
        Amounts save on blur. Clearing a cell removes that month's entry. All edits are audited.
        Months run newest-first — scroll right for older ones, or widen the range above.
      </Typography>
    </Box>
  )
}
